const MAX_IMAGE_DIMENSION = 1600
const JPEG_QUALITY = 0.8

/**
 * Reads an image File and resolves with a compressed JPEG data URL — downscaled
 * so its longest side is at most MAX_IMAGE_DIMENSION and re-encoded at
 * JPEG_QUALITY. Keeps community-post uploads small enough to ride the JSON
 * gateway and load fast on a renter's phone. Rejects when the file can't be
 * decoded as an image.
 *
 * @param {File} file - the user-selected image file
 * @returns {Promise<string>} a `data:image/jpeg;base64,...` data URL
 */
const compressImageToDataUrl = (file: File): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file)
    const image = new Image()

    image.onload = (): void => {
      URL.revokeObjectURL(objectUrl)

      const longestSide = Math.max(image.width, image.height)
      const scale =
        longestSide > MAX_IMAGE_DIMENSION
          ? MAX_IMAGE_DIMENSION / longestSide
          : 1
      const targetWidth = Math.round(image.width * scale)
      const targetHeight = Math.round(image.height * scale)

      const canvas = document.createElement('canvas')
      canvas.width = targetWidth
      canvas.height = targetHeight

      const context = canvas.getContext('2d')
      if (context === null) {
        reject(new Error('Could not process the image'))
        return
      }

      context.drawImage(image, 0, 0, targetWidth, targetHeight)
      resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY))
    }

    image.onerror = (): void => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('That file could not be read as an image'))
    }

    image.src = objectUrl
  })

export { compressImageToDataUrl }
